import { useState, useEffect } from 'react';
import { Save, User, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../hooks/useAuth';
import { FormField } from '../components/ui/FormField';
import { validatePhone } from '../utils/fieldValidation';

interface CarrierProfile {
    carrierName: string;
    mcNumber: string;
    email: string;
    phone: string;
}

const firstNonBlank = (...values: Array<string | null | undefined>): string => {
    for (const val of values) {
        if (typeof val === 'string' && val.trim().length > 0) {
            return val.trim();
        }
    }
    return '';
};

export const CarrierSettings = () => {
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState<CarrierProfile>({
        carrierName: '',
        mcNumber: '',
        email: '',
        phone: ''
    });

    const activeCarrierEmail = user?.role === 'carrier' && user?.email ? user.email.trim().toLowerCase() : '';
    const STORAGE_KEY = activeCarrierEmail ? `baywhiz_carrier_profile_v1:${activeCarrierEmail}` : '';

    useEffect(() => {
        if (!activeCarrierEmail || !STORAGE_KEY) {
            setProfile({
                carrierName: '',
                mcNumber: '',
                email: '',
                phone: '',
            });
            return;
        }
        let parsed: Partial<CarrierProfile> = {};
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const candidate = JSON.parse(saved);
                const candidateEmail = typeof candidate?.email === 'string' ? candidate.email.trim().toLowerCase() : '';
                const candidateMcDigits = typeof candidate?.mcNumber === 'string' ? candidate.mcNumber.replace(/[^0-9]/g, '') : '';
                const userMcDigits = (user?.mcNumber || '').replace(/[^0-9]/g, '');
                const emailMatches = !candidateEmail || candidateEmail === activeCarrierEmail;
                const mcMatches = !candidateMcDigits || !userMcDigits || candidateMcDigits === userMcDigits;
                if (emailMatches && mcMatches) {
                    parsed = candidate;
                }
            } catch (e) {
                console.error("Failed to parse carrier profile", e);
            }
        }
        setProfile({
            carrierName: firstNonBlank(parsed.carrierName, user?.companyName),
            mcNumber: firstNonBlank(parsed.mcNumber, user?.mcNumber),
            email: firstNonBlank(parsed.email, user?.email),
            phone: firstNonBlank(parsed.phone, user?.phone),
        });
    }, [activeCarrierEmail, user?.email, user?.companyName, user?.mcNumber, user?.phone, STORAGE_KEY]);

    const handleSave = () => {
        if (!profile.carrierName.trim()) {
            toast.error('Carrier Name is required');
            return;
        }

        if (!profile.email.trim()) {
            toast.error('Email is required');
            return;
        }
        if (!profile.phone.trim()) {
            toast.error('Phone number is required');
            return;
        }
        const phoneErr = validatePhone(profile.phone);
        if (phoneErr) {
            toast.error(phoneErr);
            return;
        }

        setLoading(true);
        // Simulate save delay for UX
        setTimeout(() => {
            if (STORAGE_KEY) {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({
                        carrierName: profile.carrierName.trim(),
                        mcNumber: firstNonBlank(user?.mcNumber, profile.mcNumber),
                        email: profile.email.trim(),
                        phone: profile.phone.trim(),
                    })
                );
            }
            toast.success('Carrier profile saved');
            setLoading(false);
        }, 500);
    };

    return (
        <div className="p-6 max-w-4xl mx-auto min-h-screen pb-20">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                    <Truck className="w-8 h-8 text-primary" />
                </div>
                <div>
                    <h1 className="text-3xl font-bold text-foreground tracking-tight">Carrier Settings</h1>
                    <p className="text-muted-foreground">Manage your auto-fill preferences for appointment requests</p>
                </div>
            </div>

            <div className="card p-6 border-l-4 border-l-primary">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <User className="text-primary" /> Profile Information
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                        label="Company / Carrier Name"
                        value={profile.carrierName}
                        onChange={(val) => setProfile(p => ({ ...p, carrierName: val }))}
                        placeholder="e.g. ABC Logistics"
                    />

                    <div className="form-group">
                        <label className="label">MC Number</label>
                        <div className="field bg-muted/50 cursor-not-allowed opacity-75">
                            {user?.mcNumber || profile.mcNumber || '—'}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            MC number is locked after signup. Contact support to change.
                        </p>
                    </div>

                    <FormField
                        label="Default Email"
                        type="email"
                        value={profile.email}
                        onChange={(val) => setProfile(p => ({ ...p, email: val }))}
                        placeholder="dispatch@carrier.com"
                    />

                    <FormField
                        label="Default Phone"
                        type="tel"
                        value={profile.phone}
                        onChange={(val) => setProfile(p => ({ ...p, phone: val }))}
                        placeholder="e.g. 555-0199"
                    />
                </div>

                <div className="flex justify-end pt-6 mt-6 border-t border-border">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        {loading ? 'Saving...' : (
                            <>
                                <Save className="w-4 h-4" />
                                Save Profile
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CarrierSettings;
